class SettingsHelper extends MIOObject
{
    private static _sharedInstance: SettingsHelper = null;
    static sharedInstance(): SettingsHelper {

        if (this._sharedInstance == null) {
            this._sharedInstance = new SettingsHelper();
            this._sharedInstance.init();
        }

        return this._sharedInstance;
    }

    init(){
        super.init();
        MIONotificationCenter.defaultCenter().addObserver(this, MWSPersistentStoreDidUpdateEntity, this.entityDidUpdateNotification);
    }

    private _config = null;
    get config(){
        return this._config;
    }

    private _isNewServersLoaded = false;
    get isNewServersLoaded(){
        return this._isNewServersLoaded;
    }

    private _isNewSync = false;
    get isNewSync() { return this._isNewSync; }

    private _ignoreServerAuthURLs = false;
    get ignoreServerAuthURLs():boolean {
        return this._ignoreServerAuthURLs;
    }

    private _ignoreFlatResult = false;
    get ignoreFlatResult():boolean {
        return this._ignoreFlatResult;
    }

    // "shell": the global login + workspace picker that embeds the cluster managers.
    // "manager": a full manager, deployed on a cluster. See docs/CLUSTER_SHELL_PLAN.md.
    private _appMode = "manager";
    get appMode():string {
        return this._appMode;
    }

    get isShell():boolean {
        return this._appMode == "shell";
    }

    // Origins allowed to embed this manager and hand a session over (manager mode).
    private _shellOrigins:string[] = [];
    get shellOrigins():string[] {
        return this._shellOrigins;
    }

    // Dev override of the cluster manager URL derived from the business urls (shell mode).
    private _clusterManagerURL:string = null;
    get clusterManagerURL():string {
        return this._clusterManagerURL;
    }

    overrideAppMode(mode:string) {
        if (mode == "shell" || mode == "manager") this._appMode = mode;
    }


    loadAppSettings(target?, completion?){

        let appData = MIOBundle.mainBundle().pathForResourceOfType("app", "plist");
        let app_items = MIOPropertyListSerialization.propertyListWithData(appData, 0, 0, null);

        let languages = app_items["Languages"];
        this.supportedLanguages = Object.keys(languages || ["en"]);

        this._config = {}

        let configData = MIOBundle.mainBundle().pathForResourceOfType("config", "plist");
        this._config = MIOPropertyListSerialization.propertyListWithData(configData, 0, 0, null);

        this._isNewSync = this._config["UseNewSync"] || false;
        this._ignoreServerAuthURLs = this._config["IgnoreServerAuthURLs"] || false;
        this._ignoreFlatResult = this._config["IgnoreFlatResult"] || false;

        this._appMode = this._config["AppMode"] == "shell" ? "shell" : "manager";
        let origins = this._config["ShellOrigins"];
        this._shellOrigins = Array.isArray(origins) ? origins.slice() : ["https://manager.dual-link.com"];
        // A page may always embed itself, which is what the local dev setup does.
        if (this._shellOrigins.indexOf(window.location.origin) == -1) this._shellOrigins.push(window.location.origin);
        this._clusterManagerURL = this._config["ClusterManagerURL"] || null;

        if (this._config["UseNewServerConfigFile"] != null && this._config["UseNewServerConfigFile"] == true) {
            this._isNewServersLoaded = true; 
        }        
        else {
            for (let key of app_items) {
                let value = app_items[key];
                this._config[key] = value;
            }
        }                

        if (target != null && completion != null) completion.call(target, this._config);        
    }
    
    loadConfig(target, completion) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        DBHelper.queryObjectsWithCompletion("Configuration", null, null, [], this, function(items){
            //this._config = {}
            for (let index = 0; index < items.length; index++){
                let cfg = items[index] as Configuration;
                this._config[cfg.name] = cfg.value; // (cfg.value != null && cfg.value.length > 0) ? JSON.parse(cfg.value) : null;
            }
            
            if (target != null && completion != null) completion.call(target, this._config);
        });
    }

    loadPermissions(target, completion){

        let userID = DualLinkAuthKit.sharedInstance().userID;
        DBHelper.queryObjectsWithCompletion("User", null, MIOPredicate.predicateWithFormat("identifier == " + userID), ["reportOptions", "reportAccess", "dashboardItems"], this, function(objects){
            let usr = objects[0] as User;
            this._permissions = usr.permisions;
            if (target != null && completion != null) completion.call(target, usr);
        });
    }

    private _permissions = null;
    get permisisons(){
        return this._permissions;
    }
    
    entityDidUpdateNotification(note:MIONotification){
        let object = note.object as MIOManagedObject;
        let entityName = object.entity.name;

        if (entityName != "Configuration") return;  
        
        let cfg = object as Configuration;
        this._config[cfg.name]  = cfg.value;

        MIONotificationCenter.defaultCenter().postNotification("ConfigurationDidChange", this._config);
    }

    private _templateLanguages = null;
    get templateLanguages(){
        if (this._templateLanguages == null) {
            let langs = this._config["booking-available-language-template"] || [];
            this._templateLanguages = [];

            let languages = DBCacheHelper.sharedInstance().objectsForEntityName("Language", [MIOSortDescriptor.sortDescriptorWithKey("name", true)], null);            
            for (let index = 0; index < languages.length; index++){
                let lang = languages[index] as Language;
                if (langs.indexOf(lang.iso2) != -1) this._templateLanguages.push(lang);
            }

        }

        return this._templateLanguages;
    }

    get currentLanguage(){
        return MIOCoreGetBrowserLanguage();
    }

    private supportedLanguages = ["en"];
    get currentSupportedLanguage(){
        let lang = this.currentLanguage;
        if (this.supportedLanguages.indexOf(lang) > -1) return lang;
        if (this._config["base-language"] != null) lang = this._config["base-language"];
        if (this.supportedLanguages.indexOf(lang) > -1) return lang;
        return "en";
    }
    
    configurationValue(key:string, defaultValue?:boolean) : any {
        return this._config[ key ] ?? defaultValue;
    }

    // The server seeds booleans as JSON booleans; the Manager saves them as "true"/"false".
    // A missing or empty value takes the default.
    configurationBoolValue(key:string, defaultValue?:boolean) : boolean {
        let cfg = this._config[ key ];
        if (cfg == null || cfg === "") return defaultValue ?? false;
        return cfg === true || cfg === "true" || cfg === 1 || cfg === "1";
    }

    get configStockUIAddLineTop() : boolean {
        return this.configurationBoolValue( "add-stock-line-on-top", false );
    }

    get invoiceCustomerChangeType() : InvoiceCustomerChangeType {
        let cfg = this.configurationValue( 'invoce-customer-change-type' );
        return cfg ?? InvoiceCustomerChangeType.beforePosted;
    }

}

enum InvoiceCustomerChangeType
{
    beforePosted = 0,
    allow        = 1,
    forbidden    = 2    
}
